import { useEffect, useMemo, useState } from "react";
import { GraduationCap, Mail, Send, Eye, AlertTriangle, Loader2, Clock } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Orientation Make-Up Email panel.
 *
 * Lives on the Reports page. Lets an admin:
 *   1. Pick an orientation date (only configured dates are selectable)
 *   2. See WHO was expected vs WHO MISSED vs WHO LEFT EARLY
 *   3. Write a custom subject + message body
 *   4. Pre-flight dry-run (shows the exact list without sending)
 *   5. Send to every missed person (or a subset they pick)
 *
 * Sends use Resend via the backend — NOT auto-triggered.
 * Merge tags supported in subject + body: {name}, {first_name}, {date}.
 */
export default function OrientationMakeupPanel() {
  const [date, setDate] = useState("");
  const [availableDates, setAvailableDates] = useState([]);
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);

  const [subject, setSubject] = useState("Make-up: {date} orientation");
  const [bodyText, setBodyText] = useState(
    "Hi {first_name},\n\nWe noticed you weren't able to make our {date} orientation. " +
    "Please reply to this email so we can get you up to speed before the next session.\n\n" +
    "Thanks!\nRolling River Day Camp"
  );
  const [selectedEmails, setSelectedEmails] = useState(null); // null = "all missed"
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  // Load report whenever date changes
  useEffect(() => {
    if (!date) return;
    let cancelled = false;
    setLoadingReport(true);
    api.get(`/reports/orientation/${date}`)
      .then((r) => {
        if (cancelled) return;
        setReport(r.data);
        if (!availableDates.length && Array.isArray(r.data.available_dates)) {
          setAvailableDates(r.data.available_dates);
        }
        // Default selection = every missed person.
        setSelectedEmails(null);
        setLastResult(null);
      })
      .catch((e) => {
        if (cancelled) return;
        toast.error(e?.response?.data?.detail || "Failed to load orientation report");
        setReport(null);
      })
      .finally(() => { if (!cancelled) setLoadingReport(false); });
    return () => { cancelled = true; };
  }, [date]); // eslint-disable-line

  // First-load: fetch available dates via any orientation report endpoint
  useEffect(() => {
    if (availableDates.length || date) return;
    api.get("/reports/orientation/2026-06-06").then((r) => {
      const dates = r.data?.available_dates || [];
      setAvailableDates(dates);
      if (dates[0]) setDate(dates[0]);
    }).catch(() => { /* ignore — admin can still pick a date manually */ });
  }, []); // eslint-disable-line

  const missed = report?.missed || [];
  const leftEarly = report?.left_early || [];
  const presentList = report?.present || [];
  const plannedAbsent = report?.planned_absence || [];
  const expected = report?.expected_count || 0;
  // Effective expected = the people we ACTUALLY expect to show up.
  // Anyone with a planned absence for this date isn't expected, so don't
  // include them in the "how many should be here" number.
  const effectiveExpected = Math.max(0, expected - plannedAbsent.length);
  const reportStatus = report?.report_status || "";
  const isUpcoming = reportStatus === "upcoming";

  // Recipients we'll actually email = current `selectedEmails` set, or
  // (when null) the full missed list.
  const recipientList = useMemo(() => {
    if (selectedEmails === null) {
      return missed.filter((m) => m.email);
    }
    return missed.filter((m) => m.email && selectedEmails.includes(m.email));
  }, [selectedEmails, missed]);

  const toggleRecipient = (email) => {
    if (selectedEmails === null) {
      // First click — switch from "all" to an explicit pick that drops this one.
      const all = missed.filter((m) => m.email).map((m) => m.email);
      setSelectedEmails(all.filter((e) => e !== email));
    } else if (selectedEmails.includes(email)) {
      setSelectedEmails(selectedEmails.filter((e) => e !== email));
    } else {
      setSelectedEmails([...selectedEmails, email]);
    }
  };

  const sendEmail = async ({ dryRun }) => {
    if (!report) return;
    if (!subject.trim() || !bodyText.trim()) {
      toast.error("Subject and body are required.");
      return;
    }
    if (recipientList.length === 0) {
      toast.error("No recipients selected.");
      return;
    }
    setSending(true);
    setLastResult(null);
    try {
      const r = await api.post(`/reports/orientation/${date}/send-makeup-email`, {
        subject: subject.trim(),
        body_text: bodyText,
        recipient_emails: selectedEmails === null
          ? null  // backend defaults to all missed
          : recipientList.map((x) => x.email),
        dry_run: !!dryRun,
      });
      setLastResult(r.data);
      if (dryRun) {
        toast.success(`Dry-run: ${r.data.recipient_count} staff would receive the email.`);
      } else {
        toast.success(`Sent to ${r.data.sent}/${r.data.total_attempted} staff.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-4 sm:p-5 mb-5"
         data-testid="orientation-makeup-panel">
      <div className="flex items-center gap-2 mb-1">
        <GraduationCap className="w-5 h-5 text-indigo-600" />
        <h2 className="font-display text-base font-bold text-slate-900">
          Orientation Make-Up Emails
        </h2>
      </div>
      <p className="text-xs text-slate-500 mb-4">
        Pick an orientation day. The list below shows only staff who were <em>expected</em>{" "}
        on that date per the orientation rules. Write your message and send to anyone who missed it.
        Use <code className="font-mono">{"{name}"}</code>, <code className="font-mono">{"{first_name}"}</code>,
        or <code className="font-mono">{"{date}"}</code> as merge tags.
      </p>

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <label className="flex flex-col gap-1">
          <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">Orientation date</span>
          <select
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white min-w-[200px]"
            data-testid="orientation-date-select"
          >
            <option value="">— select —</option>
            {availableDates.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </label>
        {report && (
          <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
            <strong>Rule:</strong> {report.rule_label}
            <span className="mx-2 text-slate-400">|</span>
            <strong>Expected:</strong> {effectiveExpected}
            {plannedAbsent.length > 0 && (
              <span className="text-slate-500">
                {" "}<span className="line-through">({expected})</span> minus {plannedAbsent.length} planned off
              </span>
            )}
            <span className="mx-2 text-slate-400">|</span>
            <span className="text-emerald-700"><strong>Present:</strong> {presentList.length}</span>
            <span className="mx-2 text-slate-400">|</span>
            <span className="text-rose-700"><strong>Missed:</strong> {missed.length}</span>
            <span className="mx-2 text-slate-400">|</span>
            <span className="text-amber-700"><strong>Left early:</strong> {leftEarly.length}</span>
            {plannedAbsent.length > 0 && (
              <>
                <span className="mx-2 text-slate-400">|</span>
                <span className="text-violet-700"><strong>Planned off:</strong> {plannedAbsent.length}</span>
              </>
            )}
          </div>
        )}
      </div>

      {/* Big banner when the orientation hasn't happened yet — explains why
          "Missed" is showing every expected staff member. */}
      {isUpcoming && (
        <div className="mb-4 rounded-xl border border-indigo-300 bg-indigo-50 px-3 py-2 text-xs text-indigo-900" data-testid="orientation-upcoming-banner">
          <strong>{report?.date}</strong> hasn't happened yet (today is <strong>{report?.today}</strong>).
          The <em>Missed</em> list below is simply <strong>everyone expected</strong> — it'll start emptying out as staff sign in on the day.
          You can still draft an email now and send it after the orientation ends.
        </div>
      )}

      {loadingReport && (
        <div className="flex items-center gap-2 text-sm text-slate-500 py-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading orientation report…
        </div>
      )}

      {report && !loadingReport && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* PRESENT — confirms WHO is actually there */}
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-3">
            <div className="flex items-center gap-2 mb-2">
              <GraduationCap className="w-4 h-4 text-emerald-700" />
              <h3 className="font-bold text-sm text-emerald-800">Present ({presentList.length})</h3>
            </div>
            {presentList.length === 0 ? (
              <div className="text-xs text-slate-500 py-4 text-center">No one signed in yet.</div>
            ) : (
              <ul className="max-h-64 overflow-y-auto space-y-1" data-testid="orientation-present-list">
                {presentList.map((m) => (
                  <li key={m.crew_id} className="bg-white rounded px-2 py-1.5 border border-emerald-100">
                    <div className="text-xs font-bold text-slate-900 truncate">{m.name}</div>
                    <div className="text-[10px] text-emerald-700 font-mono truncate">In: {m.signed_in_at || "—"}</div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* MISSED LIST */}
          <div className="rounded-xl border border-rose-200 bg-rose-50/40 p-3">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-rose-700" />
              <h3 className="font-bold text-sm text-rose-800">Missed ({missed.length})</h3>
              {missed.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSelectedEmails(selectedEmails === null ? [] : null)}
                  className="ml-auto text-[11px] font-bold uppercase tracking-[0.12em] text-rose-700 underline"
                  data-testid="orientation-toggle-all"
                >
                  {selectedEmails === null ? "Deselect all" : "Select all"}
                </button>
              )}
            </div>
            {missed.length === 0 ? (
              <div className="text-xs text-slate-500 py-4 text-center">No-one missed — nice 🎉</div>
            ) : (
              <ul className="max-h-64 overflow-y-auto space-y-1" data-testid="orientation-missed-list">
                {missed.map((m) => {
                  const checked = selectedEmails === null ? true : selectedEmails.includes(m.email);
                  return (
                    <li key={m.crew_id} className="flex items-center gap-2 bg-white rounded px-2 py-1.5 border border-rose-100">
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={!m.email}
                        onChange={() => toggleRecipient(m.email)}
                        data-testid={`orientation-check-${m.crew_id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-bold text-slate-900 truncate">{m.name}</div>
                        <div className="text-[10px] text-slate-500 truncate">
                          {m.email || <span className="text-rose-600 italic">no email on file</span>}
                          {m.division ? ` · ${m.division}` : ""}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* LEFT EARLY LIST (report-only, NOT emailed by this tool) */}
          <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-3">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-amber-700" />
              <h3 className="font-bold text-sm text-amber-900">Left Early ({leftEarly.length})</h3>
              <span className="text-[10px] text-slate-500 ml-auto">Report only — not emailed</span>
            </div>
            {leftEarly.length === 0 ? (
              <div className="text-xs text-slate-500 py-4 text-center">Nobody left early.</div>
            ) : (
              <ul className="max-h-64 overflow-y-auto space-y-1">
                {leftEarly.map((m) => (
                  <li key={m.crew_id} className="bg-white rounded px-2 py-1.5 border border-amber-100">
                    <div className="text-xs font-bold text-slate-900 truncate">{m.name}</div>
                    <div className="text-[10px] text-slate-600 truncate font-mono">
                      In: {m.signed_in_at || "—"} · Left: <strong>{m.left_at || "—"}</strong>
                      {m.auto_signed_out ? " (auto)" : ""}
                    </div>
                    {m.leave_early_reason && (
                      <div className="text-[10px] text-slate-500 italic mt-0.5">{m.leave_early_reason}</div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {report && missed.length > 0 && (
        <div className="mt-5 space-y-3">
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">Subject</span>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white"
              data-testid="orientation-subject"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500 font-bold">Message body</span>
            <textarea
              rows={8}
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value)}
              className="px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white font-mono leading-relaxed"
              data-testid="orientation-body"
            />
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => sendEmail({ dryRun: true })}
              disabled={sending || recipientList.length === 0}
              className="btn-touch inline-flex items-center gap-2 px-3 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-sm disabled:opacity-50"
              data-testid="orientation-dry-run"
            >
              <Eye className="w-4 h-4" /> Preview recipients
            </button>
            <button
              onClick={() => sendEmail({ dryRun: false })}
              disabled={sending || recipientList.length === 0}
              className="btn-touch inline-flex items-center gap-2 px-4 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm disabled:opacity-50"
              data-testid="orientation-send"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? "Sending…" : `Send to ${recipientList.length}`}
            </button>
            <span className="text-xs text-slate-500 ml-1">
              <Mail className="w-3 h-3 inline-block mr-1" />
              {recipientList.length} of {missed.length} missed staff
              {missed.filter((m) => !m.email).length > 0 &&
                ` · ${missed.filter((m) => !m.email).length} have no email on file`}
            </span>
          </div>

          {lastResult && (
            <div className="text-xs rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
                 data-testid="orientation-result">
              {lastResult.dry_run ? (
                <>
                  <strong>Dry run:</strong> would send to <strong>{lastResult.recipient_count}</strong> staff.
                  Click <em>Send</em> to actually deliver.
                </>
              ) : (
                <>
                  <strong>Sent:</strong> {lastResult.sent}/{lastResult.total_attempted} delivered.
                  {lastResult.failed_recipients?.length > 0 &&
                    <span className="text-rose-700 ml-2">
                      Failed: {lastResult.failed_recipients.join(", ")}
                    </span>}
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
